import { useContext } from "react"
import { Divider, LineLoader } from "@components"
import { UserContext, NotificationContext } from "@context"
import { getDateRangeStatus, getGraphQLErrorMessage } from "@helpers"
import { useDeleteSession, useSessionBookingCounts } from "@hooks"
import { useApolloClient } from "@apollo/client";
import { Button } from "@heroui/react"
import PropTypes from "prop-types"

import SessionErrorPlaceholder from "../SessionErrorPlaceholder"

const DeleteSession = ({ event, handleCancel, session }) => 
{
  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)
  const { setCurrentEvent } = useContext(UserContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const { deleteSession, loading: deleting } = useDeleteSession()

  const { data: bookingCountData, error: bookingCountError, loading: loadingBookingCount } = useSessionBookingCounts({
    variables: { id: session.id },
    fetchPolicy: 'network-only'
  })

  /**
   * Functions
   */
  const handleDelete = () => {
    deleteSession({
      variables: {
        input: {
          id: session.id,
        },
      },

      onCompleted: (response) => {
        setCurrentEvent(event.id) // forces user context to refresh event data
        notify({ title: "Success", type: "success", content: `Session deleted successfully.`, open: true })
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session
        handleCancel()
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, `An error occurred while deleting the session.`), open: true })
      }
    })
  }

  /**
   * Render
   */
  if (loadingBookingCount) {
    return <LineLoader message="Please wait, loading..." classNames="mt-8" />
  }

  const eventStatus = getDateRangeStatus(session.starts_at, session.ends_at, "milliseconds")
  const isEventLiveOrExpired = eventStatus === "Current" || eventStatus === "Expired"
  const bookingCount = bookingCountData?.session?.no_of_bookings || 0
  const hasBookings = bookingCount > 0
  
  if (bookingCountError) {
    return (
      <SessionErrorPlaceholder 
        title="Session Deletion Disabled" 
        description="You cannot delete this session because we were unable to verify if there are any bookings for this session. Please try again later." 
        handleCancel={handleCancel} 
      />
    )
  }

  if (isEventLiveOrExpired) {
    return (
      <SessionErrorPlaceholder 
        title="Session Deletion Disabled" 
        description="You cannot delete this session because the session is either live or has expired." 
        handleCancel={handleCancel} 
      />
    )
  }
  
  if ((event.sessions?.length ?? 0) <= 1) {
    return (
      <SessionErrorPlaceholder 
        title="Session Deletion Disabled" 
        description="You cannot delete this session because it is the only session in the event." 
        handleCancel={handleCancel} 
      />
    )
  }

  if (hasBookings) {
    return (
      <SessionErrorPlaceholder 
        title="Session Deletion Disabled" 
        description={`You cannot delete this session as it has bookings. ${bookingCount} booking(s) are linked to this session.`} 
        handleCancel={handleCancel} 
      />
    )
  }

  return (
    <div>      
      <h2 className="mb-2 text-lg font-semibold">{session.title}</h2>
      <p className="text-base text-danger">
        Are you sure you wish to delete this session? All session data will be deleted. This cannot be undone!
      </p>

      <Divider className="my-4" />
      
      <Button 
        className="mb-2 font-semibold" 
        color="danger" 
        fullWidth 
        onPress={handleDelete} isLoading={deleting}
      >
        Delete Session
      </Button>
      
      <Button color="default" fullWidth onPress={handleCancel} isDisabled={deleting}>Cancel</Button>
    </div>
  )
}

DeleteSession.propTypes = {
  event: PropTypes.object.isRequired,
  handleCancel: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired
}

export default DeleteSession